"use client";

import type { ReactNode } from "react";
import {
  BrainCircuit,
  Clock3,
  Info,
  MessageSquareText,
  Mic2,
  Phone,
  Save,
  Shield,
  Video,
  X,
  Layers,
  ScrollText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import type { Agent } from "@/lib/api";
import { agentModeLabel, normalizeAgentMode } from "@/lib/agent-mode";
import { alternateAgentAvatarUrl } from "@/lib/agent-media";

export type AgentEditSectionId =
  | "conversation"
  | "endOfCall"
  | "guards"
  | "endpointing"
  | "callControl"
  | "sessionLimits"
  | "voice"
  | "videoAvatar"
  | "perception";

export const AGENT_EDIT_NAV: Array<{
  id: AgentEditSectionId;
  label: string;
  icon: ReactNode;
}> = [
  { id: "conversation", label: "Conversation", icon: <MessageSquareText size={16} strokeWidth={1.75} /> },
  { id: "endOfCall", label: "End of call", icon: <ScrollText size={16} strokeWidth={1.75} /> },
  { id: "guards", label: "Guards", icon: <Shield size={16} strokeWidth={1.75} /> },
  { id: "endpointing", label: "End of turn", icon: <Clock3 size={16} strokeWidth={1.75} /> },
  { id: "callControl", label: "Call control", icon: <Phone size={16} strokeWidth={1.75} /> },
  { id: "sessionLimits", label: "Session limits", icon: <Layers size={16} strokeWidth={1.75} /> },
  { id: "voice", label: "Voice", icon: <Mic2 size={16} strokeWidth={1.75} /> },
  { id: "videoAvatar", label: "Video avatar", icon: <Video size={16} strokeWidth={1.75} /> },
  { id: "perception", label: "Perception", icon: <BrainCircuit size={16} strokeWidth={1.75} /> },
];

export const AGENT_EDIT_NAV_DIRECT: Array<{
  id: AgentEditSectionId;
  label: string;
  icon: ReactNode;
}> = [
  { id: "conversation", label: "Conversation", icon: <MessageSquareText size={16} strokeWidth={1.75} /> },
  { id: "endOfCall", label: "End of call", icon: <ScrollText size={16} strokeWidth={1.75} /> },
  { id: "guards", label: "Guards", icon: <Shield size={16} strokeWidth={1.75} /> },
  { id: "callControl", label: "Call control", icon: <Phone size={16} strokeWidth={1.75} /> },
  { id: "sessionLimits", label: "Session limits", icon: <Layers size={16} strokeWidth={1.75} /> },
  { id: "voice", label: "Voice", icon: <Mic2 size={16} strokeWidth={1.75} /> },
  { id: "videoAvatar", label: "Video avatar", icon: <Video size={16} strokeWidth={1.75} /> },
  { id: "perception", label: "Perception", icon: <BrainCircuit size={16} strokeWidth={1.75} /> },
];

/** LiveAvatar FULL: no End of turn; call-control hangup/idle tools hidden in page body. */
export const AGENT_EDIT_NAV_LIVEAVATAR: Array<{
  id: AgentEditSectionId;
  label: string;
  icon: ReactNode;
}> = [
  { id: "conversation", label: "Conversation", icon: <MessageSquareText size={16} strokeWidth={1.75} /> },
  { id: "endOfCall", label: "End of call", icon: <ScrollText size={16} strokeWidth={1.75} /> },
  { id: "sessionLimits", label: "Session limits", icon: <Layers size={16} strokeWidth={1.75} /> },
  { id: "voice", label: "Voice", icon: <Mic2 size={16} strokeWidth={1.75} /> },
  { id: "videoAvatar", label: "Video avatar", icon: <Video size={16} strokeWidth={1.75} /> },
  { id: "perception", label: "Perception", icon: <BrainCircuit size={16} strokeWidth={1.75} /> },
];

function AgentAvatarImg({
  src,
  avatarKey,
  alt = "",
}: {
  src: string;
  avatarKey?: string | null;
  alt?: string;
}) {
  return (
    <img
      src={src}
      alt={alt}
      onError={(e) => {
        const el = e.currentTarget;
        if (el.dataset.fallbackTried) return;
        const next = alternateAgentAvatarUrl(el.src, avatarKey);
        if (!next) return;
        el.dataset.fallbackTried = "1";
        el.src = next;
      }}
    />
  );
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: Agent | null;
  /** Catalog row cover / published meta when draft avatar lags. */
  avatarUrl?: string | null;
  avatarKey?: string | null;
  /** Catalog role when draft.role is empty. */
  roleFallback?: string | null;
  /** Working-copy publish badge (Production vs Draft). */
  published?: boolean;
  /** Soft-archived agents show Archived instead of Production/Draft. */
  archived?: boolean;
  /** Selected editor target: working copy or a published snapshot number. */
  viewingVersion: "working" | number;
  /** Published snapshots for the version selector (newest first). */
  versionOptions?: Array<{ version: number; publishedAt?: string | null }>;
  onViewingVersionChange?: (next: "working" | number) => void;
  section: AgentEditSectionId;
  onSectionChange: (id: AgentEditSectionId) => void;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  /** Clone the loaded version (working or published snapshot) into a new agent. */
  onClone?: () => void;
  /** Admin-only: open Pulse Test call for the version currently shown in the editor. */
  onTestCall?: () => void;
  /** Tab body — empty placeholder until section UIs land. */
  children?: ReactNode;
};

export function AgentEditModal({
  open,
  onOpenChange,
  draft,
  avatarUrl,
  avatarKey,
  roleFallback,
  published = false,
  archived = false,
  viewingVersion,
  versionOptions = [],
  onViewingVersionChange,
  section,
  onSectionChange,
  dirty,
  saving,
  onSave,
  onClone,
  onTestCall,
  children,
}: Props) {
  const name = draft?.name || "Agent";
  const role = (draft?.role || "").trim() || (roleFallback || "").trim() || "Untitled role";
  const cover = draft?.avatarUrl || avatarUrl || null;
  const coverKey = draft?.avatarKey || avatarKey || null;
  const viewingHistorical = viewingVersion !== "working";
  const showProduction = !viewingHistorical && published && !dirty;
  const selectValue = viewingVersion === "working" ? "working" : String(viewingVersion);
  const agentMode = normalizeAgentMode(draft?.agentMode);
  const nav =
    agentMode === "liveavatar"
      ? AGENT_EDIT_NAV_LIVEAVATAR
      : agentMode === "direct"
        ? AGENT_EDIT_NAV_DIRECT
        : AGENT_EDIT_NAV;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="agent-edit-dialog flex flex-col bg-white p-0 gap-0 overflow-hidden border-0"
        aria-describedby={undefined}
      >
        <header className="agent-edit-header">
          <div className="agent-edit-identity">
            <div className={`agent-edit-avatar${cover ? "" : " is-empty"}`}>
              {cover ? (
                <AgentAvatarImg src={cover} avatarKey={coverKey} alt="" />
              ) : (
                <span aria-hidden>{name.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="agent-edit-identity-copy">
              <DialogTitle className="agent-edit-title">Edit {name}</DialogTitle>
              <div className="agent-edit-meta-row">
                <DialogDescription className="agent-edit-role" title={role}>{role}</DialogDescription>
                <span
                  className={
                    agentMode === "liveavatar"
                      ? "agent-edit-badge agent-edit-badge--liveavatar"
                      : agentMode === "direct"
                        ? "agent-edit-badge agent-edit-badge--direct"
                        : "agent-edit-badge agent-edit-badge--cascade"
                  }
                >
                  {agentModeLabel(agentMode)}
                </span>
                {archived ? (
                  <span className="agent-edit-badge agent-edit-badge--draft">Archived</span>
                ) : viewingHistorical ? (
                  <span className="agent-edit-badge agent-edit-badge--live">
                    <i />
                    Published
                  </span>
                ) : showProduction ? (
                  <span className="agent-edit-badge agent-edit-badge--live">
                    <i />
                    Production
                  </span>
                ) : (
                  <span className="agent-edit-badge agent-edit-badge--draft">Draft</span>
                )}
                <Select
                  className="agent-edit-version-select"
                  ariaLabel="Agent version"
                  searchable={versionOptions.length > 7}
                  disabled={saving || !onViewingVersionChange}
                  value={selectValue}
                  onChange={(v) => {
                    if (!v) return;
                    onViewingVersionChange?.(v === "working" ? "working" : Number(v));
                  }}
                  options={[
                    { value: "working", label: "Working copy" },
                    ...versionOptions.map((v) => ({
                      value: String(v.version),
                      label: `v${v.version}`,
                      description: v.publishedAt
                        ? new Date(v.publishedAt).toLocaleString()
                        : undefined,
                    })),
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="agent-edit-header-actions">
            <div className="agent-edit-info-banner" role="note">
              <Info size={14} strokeWidth={2} aria-hidden />
              <span>Changes apply to new sessions only.</span>
            </div>
            <button
              type="button"
              className="agent-edit-close"
              aria-label="Close"
              onClick={() => onOpenChange(false)}
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
        </header>

        <div className="agent-edit-body">
          <nav className="agent-edit-nav" aria-label="Agent configuration sections">
            <ul>
              {nav.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={section === item.id ? "is-active" : undefined}
                    title={item.label}
                    onClick={() => onSectionChange(item.id)}
                  >
                    <span className="agent-edit-nav-icon" aria-hidden>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="agent-edit-panel">
            {children ?? (
              <div className="agent-edit-panel-placeholder" aria-hidden>
                {/* Section content ships in follow-up passes */}
              </div>
            )}
          </div>
        </div>

        <footer className="agent-edit-footer">
          <div className="agent-edit-footer-start">
            <button
              type="button"
              className="agent-edit-btn agent-edit-footer-clone"
              disabled={saving || !draft || !onClone}
              title={
                viewingVersion === "working"
                  ? "Create a new agent from this working copy"
                  : `Create a new agent from published v${viewingVersion}`
              }
              onClick={() => onClone?.()}
            >
              Clone as new agent
            </button>
            <button
              type="button"
              className="agent-edit-btn"
              disabled={saving || !draft || !onTestCall || archived}
              title={
                archived
                  ? "Unarchive before testing"
                  : viewingVersion === "working"
                    ? "Admin Test call with this working draft (unpublished OK)"
                    : `Admin Test call with published v${viewingVersion}`
              }
              onClick={() => onTestCall?.()}
            >
              <Video size={15} strokeWidth={2} aria-hidden />
              Test call
            </button>
          </div>
          <div className="agent-edit-footer-end">
            <div className="agent-edit-dirty" data-state={dirty ? "dirty" : "clean"}>
              {dirty ? (
                <>
                  <div className="agent-edit-dirty-title">
                    <i />
                    Unsaved changes
                  </div>
                  <p>You have made changes to this agent&apos;s configuration.</p>
                </>
              ) : (
                <p className="agent-edit-dirty-clean">No unsaved changes</p>
              )}
            </div>
            <div className="agent-edit-footer-actions">
              <button
                type="button"
                className="agent-edit-btn"
                onClick={() => onOpenChange(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="agent-edit-btn agent-edit-btn--primary"
                disabled={saving || !draft || !dirty}
                onClick={() => void onSave()}
              >
                <Save size={15} strokeWidth={2} aria-hidden />
                Save configuration
              </button>
            </div>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
